import { WalkthroughGroup } from '@mesa/core/pull';
import { FolderIcon, FolderOpenIcon } from '@phosphor-icons/react';
import { CaretRightIcon, CaretDownIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  ArrowDownNarrowWideIcon,
  FileIcon,
  FileInputIcon,
  FileMinusIcon,
  FilePlusIcon,
  SparklesIcon,
} from 'lucide-react';
import { memo, Suspense, useMemo, useState, useEffect} from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Skeleton } from './ui/skeleton';

import { GithubFileData, GithubFileStatus, pullFilesQuery } from '@/features/pulls/services/pulls';
import { cn } from '@/lib/utils';


declare global {
  interface Window {
    scrollToFile?: (filePath: string) => void;
    scrollToHeading?: (heading: string) => void;
    openFile?: (filePath: string) => void;
  }
}

type FileStatusIconProps = {
  status: GithubFileStatus;
  className?: string;
};

const FileStatusIcon = memo(({ status, className }: FileStatusIconProps) => {
  const iconProps = cn('size-4 fill-stone-50', className);

  switch (status) {
    case 'added':
      return <FilePlusIcon className={cn(iconProps, 'text-emerald-600')} />;
    case 'removed':
      return <FileMinusIcon className={cn(iconProps, 'text-rose-600')} />;
    case 'renamed':
      return <FileInputIcon className={cn(iconProps, 'text-blue-600')} />;
    default:
      return <FileIcon className={iconProps} />;
  }
});

interface TreeNode {
  name: string;
  fullPath: string;
  children: Map<string, TreeNode>;
  file?: GithubFileData;
  isDirectory: boolean;
  isWalkthroughGroup?: boolean;
  groupHeading?: string;
}

function buildFileTree(files: GithubFileData[]): TreeNode[] {
  const root: TreeNode = {
    name: '',
    fullPath: '',
    children: new Map(),
    isDirectory: true,
  };

  // Build tree structure
  for (const file of files) {
    const parts = file.filename.split('/');
    let current = root;

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      const fullPath = parts.slice(0, index + 1).join('/');

      if (!current.children.has(part)) {
        current.children.set(part, {
          name: part,
          fullPath,
          children: new Map(),
          file: isLast ? file : undefined,
          isDirectory: !isLast,
        });
      }
      current = current.children.get(part)!;
    });
  }

  // Collapse single-child directories
  function collapse(node: TreeNode): TreeNode {
    if (!node.isDirectory) return node;

    const children = new Map(Array.from(node.children.entries()).map(([k, v]) => [k, collapse(v)]));

    if (children.size === 1 && node.name) {
      const [_, child] = Array.from(children.entries())[0];
      if (child.isDirectory) {
        return {
          ...child,
          name: `${node.name}/${child.name}`,
          children: child.children,
        };
      }
    }

    return { ...node, children };
  }

  return Array.from(collapse(root).children.values());
}

function buildWalkthroughTree(walkthrough: WalkthroughGroup[]): TreeNode[] {
  return walkthrough.map((group) => {
    // Root node for the walkthrough group
    const groupNode: TreeNode = {
      name: group.heading,
      fullPath: group.heading,
      children: new Map(),
      isDirectory: true,
      isWalkthroughGroup: true,
      groupHeading: group.heading,
    };

    // Convert the walkthrough group's files to a minimal GithubFileData shape so we can
    // leverage the existing `buildFileTree` helper to create a directory/file tree.
    const groupFiles = group.files.map((file) => {
      const filename = file.type === 'delete' ? file.oldPath : file.newPath;

      return {
        sha: 'dummy-sha',
        filename,
        status: file.type === 'add' ? 'added' : file.type === 'delete' ? 'removed' : 'modified',
        additions: file.hunks.reduce((acc, hunk) => acc + hunk.newLines, 0),
        deletions: file.hunks.reduce((acc, hunk) => acc + hunk.oldLines, 0),
        changes: file.hunks.reduce((acc, hunk) => acc + hunk.newLines + hunk.oldLines, 0),
        blob_url: '',
        raw_url: '',
        contents_url: '',
        patch: '',
      } as unknown as GithubFileData;
    });

    // Build a nested tree for this group's files
    const childNodes = buildFileTree(groupFiles);

    // Attach the generated children to the group node
    childNodes.forEach((child) => {
      groupNode.children.set(child.fullPath, child);
    });

    return groupNode;
  });
}

interface TreeNodeViewProps {
  node: TreeNode;
  filterText: string;
  depth?: number;
}

const TreeNodeView = memo(({ node, filterText, depth = 0 }: TreeNodeViewProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const matchesFilter = useMemo(() => {
    if (!filterText) return true;
    const searchText = filterText.toLowerCase();

    function matches(n: TreeNode): boolean {
      return n.fullPath.toLowerCase().includes(searchText) || Array.from(n.children.values()).some(matches);
    }

    return matches(node);
  }, [node, filterText]);

  if (!matchesFilter) return null;

  const toggleExpand = () => setIsExpanded((prev) => !prev);
  const childNodes = Array.from(node.children.values());

  const handleNodeClick = () => {
    if (node.isWalkthroughGroup) {
      // For walkthrough groups, clicking the row scrolls to the heading
      window.scrollToHeading?.(node.groupHeading || '');
      return;
    }

    if (node.isDirectory) {
      toggleExpand();
    } else {
      // For files: scroll to the file AND open it if closed
      window.scrollToFile?.(node.fullPath);
      window.openFile?.(node.fullPath);
    }
  };

  const hasChildren = childNodes.length > 0;

  return (
    <div className="w-full">
      <div
        onMouseDown={handleNodeClick}
        className={cn(
          'grid grid-cols-[var(--indent-width)_20px_1fr] items-center py-1.5 hover:bg-sky-400/10 cursor-pointer transition-colors gap-1',
          // (node.isDirectory ) && 'font-medium',
          node.isWalkthroughGroup && 'font-semibold'
        )}
        style={{ '--indent-width': `${depth * 16 + 8}px` } as React.CSSProperties}
      >
        {/* Indentation spacer */}
        <div />

        {/* Single icon */}
        <div className="flex items-center justify-center">
          {node.isWalkthroughGroup ? (
            // Section headers: chevron icon
            hasChildren ? (
              <span
                onMouseDown={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
                className="flex h-4 w-4 items-center justify-center cursor-pointer transition-colors hover:text-gray-600"
              >
                {isExpanded ? (
                  <CaretDownIcon className="h-3.5 w-3.5 text-gray-400" />
                ) : (
                  <CaretRightIcon className="h-3.5 w-3.5 text-gray-400" />
                )}
              </span>
            ) : null
          ) : node.isDirectory ? (
            // Folders: folder icon only
            isExpanded ? (
              <FolderOpenIcon className="h-4 w-4 text-sky-500" weight="fill" />
            ) : (
              <FolderIcon className="h-4 w-4 text-sky-500" weight="fill" />
            )
          ) : (
            // Files: file status icon
            <FileStatusIcon status={node.file?.status ?? 'unchanged'} className="h-4 w-4" />
          )}
        </div>

        {/* Filename */}
        <span className="truncate text-xs text-gray-700 select-none min-w-0" title={node.name}>
          {node.name}
        </span>
      </div>

      {/* Children */}
      {isExpanded && (node.isDirectory || node.isWalkthroughGroup) && hasChildren && (
        <div className="relative">
          {/* Vertical line for tree hierarchy */}
          <div className="absolute inset-y-0 w-px bg-gray-200 z-10" style={{ left: `${depth * 16 + 20}px` }} />
          {childNodes.map((child) => (
            <TreeNodeView key={child.fullPath} node={child} filterText={filterText} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
});

type FileTreeProps = {
  sortByRelevancy: boolean;
  isGenerating: boolean;
  walkthrough: WalkthroughGroup[];
  onSortChange: (sortByRelevancy: boolean) => void;
};

const FileTree = memo(({ sortByRelevancy, walkthrough, onSortChange}: FileTreeProps) => {
  const [filterText, setFilterText] = useState('');
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const [{ data: files }] = useSuspenseQueries({
    queries: [pullFilesQuery(owner, repo, number)],
  });

  const treeNodes = useMemo(() => {
    if (sortByRelevancy && walkthrough.length > 0) {
      return buildWalkthroughTree(walkthrough);
    } else {
      return buildFileTree(files as GithubFileData[]);
    }
  }, [files, sortByRelevancy, walkthrough]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2 px-2 items-center relative">
        <MagnifyingGlassIcon className="absolute left-4.5 top-1/2 size-4 -translate-y-1/2 text-gray-500" />
        <Input
          placeholder="Filter changed files"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="h-8 pl-9 text-xs shadow-none inset-shadow-2xs focus-visible:ring-0 focus-visible:inset-ring focus-visible:inset-ring-sky-500"
        />
      </div>
      <div className="flex flex-row gap-2 px-2 items-center">
        <Separator className="w-full flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="hover:cursor-pointer">
            <ArrowDownNarrowWideIcon className="size-4 text-gray-500" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                <span className="text-xs text-gray-500">Sort By</span>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuRadioGroup
              value={sortByRelevancy ? 'relevancy' : 'alphabetical'}
              onValueChange={(value) => onSortChange(value === 'relevancy')}
            >
              <DropdownMenuRadioItem value="relevancy">
                <span className="text-xs text-gray-500">Relevancy</span>{' '}
                <SparklesIcon className="size-3 text-amber-500 shrink-0" />
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="alphabetical">
                <span className="text-xs text-gray-500">A-Z</span>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="text-xs">
        {treeNodes.map((node) => (
          <TreeNodeView key={node.fullPath} node={node} filterText={filterText} />
        ))}
      </div>
    </div>
  );
});

// Backward compatibility wrapper
export const FilePathsTree = (props: FileTreeProps) => {
  return (
    <Suspense
      fallback={
        <div className="flex flex-col pt-2 px-2 gap-2 h-full">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="w-full h-full" />
        </div>
      }
    >
      <FileTree {...props} />
    </Suspense>
  );
};
